import { PAYSLIP_STATUSES } from './payslipStatus';
import type { EmployeePayslipListQuery } from './types';

/** The Employee Payslips list is server-driven (EPN-237): the filters below are sent to `GET /api/reports/employee-payslips`,
 * which pages, filters and summarises. This module only holds the pure UI-side helpers. */
export interface PayslipFilters {
  search: string;
  employeeId: string;
  /** A payroll run id (the "Select Pay Period" control). */
  payPeriod: string;
  payDay: string;
  from: string;
  to: string;
  status: string;
  /** `DirectDeposit` | `Check` | '' (all). */
  payMethod: string;
}

export const NO_FILTERS: PayslipFilters = { search: '', employeeId: '', payPeriod: '', payDay: '', from: '', to: '', status: '', payMethod: '' };

export function filtersActive(f: PayslipFilters): boolean {
  return (Object.keys(f) as (keyof PayslipFilters)[]).some((key) => f[key].trim() !== '');
}

/** The status filter offers every status the server can derive. */
export const STATUS_FILTER_OPTIONS: readonly string[] = PAYSLIP_STATUSES;

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50] as const;

/** EPN-239: the orderings the server accepts for `sort` (`<field>:<direction>`); anything else is a 400. Names sort A to Z / Z to A, pay dates newest / oldest first. */
export const PAYSLIP_SORTS = ['checkDate:desc', 'checkDate:asc', 'employeeName:asc', 'employeeName:desc'] as const;
export type PayslipSort = (typeof PAYSLIP_SORTS)[number];
export type PayslipSortField = 'employeeName' | 'checkDate';
/** Pay date, newest first: what the server does when no `sort` is sent. */
export const DEFAULT_SORT: PayslipSort = 'checkDate:desc';

/** The `aria-sort` value of a sortable column header for the current sort. */
export function ariaSortFor(current: PayslipSort, field: PayslipSortField): 'ascending' | 'descending' | 'none' {
  if (!current.startsWith(`${field}:`)) return 'none';
  return current.endsWith(':asc') ? 'ascending' : 'descending';
}

/** What a click on a column header selects. Names start at A to Z and then toggle to Z to A; a pay date starts newest first and then toggles to oldest first.
 * Choosing the other column starts that column at its own first direction. */
export function nextSort(current: PayslipSort, field: PayslipSortField): PayslipSort {
  if (field === 'employeeName') return current === 'employeeName:asc' ? 'employeeName:desc' : 'employeeName:asc';
  return current === 'checkDate:desc' ? 'checkDate:asc' : 'checkDate:desc';
}

export function pageCount(total: number, rowsPerPage: number): number {
  return Math.max(1, Math.ceil(total / rowsPerPage));
}

/** Builds the server query. "Pay Day" and "Date Range" both constrain the pay date, so they are intersected; returns
 * null when the intersection is empty (the server would reject `to < from`, and nothing can match anyway). Dates are
 * ISO `YYYY-MM-DD`, compared lexicographically. */
export function toListQuery(f: PayslipFilters, page: number, pageSize: number, sort: PayslipSort = DEFAULT_SORT): EmployeePayslipListQuery | null {
  let from = f.from;
  let to = f.to;
  if (f.payDay) {
    if (!from || f.payDay > from) from = f.payDay;
    if (!to || f.payDay < to) to = f.payDay;
  }
  if (from && to && from > to) return null;
  const query: EmployeePayslipListQuery = { page, pageSize };
  if (from) query.payDateFrom = from;
  if (to) query.payDateTo = to;
  if (f.status) query.status = f.status;
  if (f.payMethod) query.payMethod = f.payMethod;
  if (f.search.trim()) query.search = f.search.trim();
  if (f.payPeriod) query.payrollRunId = f.payPeriod;
  if (f.employeeId) query.employeeId = f.employeeId;
  // The default order is the server's own, so it is not sent (older servers that do not know `sort` keep working).
  if (sort !== DEFAULT_SORT) query.sort = sort;
  return query;
}
